import React from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Heart, ChevronRight, Building2 } from "lucide-react";
import { sharedApi } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import SellerFeedbackWidget from "@/components/detail/SellerFeedbackWidget";

/**
 * "About the seller" card (iter-227 mirror form). Reads the `seller`
 * sub-object served by `/listings/:id`:
 *   { id, name, photo, member_since, location,
 *     feedback_percent, feedback_count, other_listings }
 * plus the top-level `is_favourite_seller` flag for the current user.
 * Fields fold silently when absent so older backends don't break the page.
 * Favouriting uses `/classifieds/follows` (existing follows API, unified
 * with iter-227 mirror-form spec — no bespoke favourite endpoint).
 */
function initialOf(name) {
  return String(name || "?").trim().charAt(0).toUpperCase();
}

function memberSince(dateStr) {
  if (!dateStr) return null;
  try {
    return new Date(dateStr).toLocaleDateString("en-NZ", {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return null;
  }
}

export default function SellerCard({ listing }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const s = listing.seller || {};
  const sellerId = s.id || listing.seller_id || listing.user_id;
  const sellerName = s.name || listing.seller_name || "Seller";
  // Iter-274 W6 — Business badge. Backend serves `seller.is_business`
  // on the listing-detail seller sub-object; the listing doc may also
  // carry a flat `seller_is_business` for legacy payloads.
  const isBusiness = !!(s.is_business ?? listing.seller_is_business);
  const location = s.location || listing.seller_location || listing.city || listing.region;
  const feedbackPct = s.feedback_percent ?? listing.seller_feedback_pct;
  const feedbackCount = s.feedback_count ?? listing.seller_feedback_count;
  const memberSinceLabel = memberSince(s.member_since || listing.seller_created_at);
  const otherCount = s.other_listings;
  const isFavourite = !!listing.is_favourite_seller;
  const followId = listing.favourite_follow_id;

  const favMut = useMutation({
    mutationFn: async () => {
      if (isFavourite && followId) {
        await sharedApi.delete(`/classifieds/follows/${followId}`);
      } else {
        await sharedApi.post("/classifieds/follows", { seller_id: sellerId });
      }
      return { toggledTo: !isFavourite };
    },
    onSuccess: ({ toggledTo }) => {
      toast.success(toggledTo ? "Added to Favourite Sellers." : "Removed from Favourite Sellers.");
      qc.invalidateQueries({ queryKey: ["ad", listing.id] });
    },
    onError: (err) => {
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        toast.info("Sign in to save Favourite Sellers.");
      } else {
        toast.error(err?.response?.data?.detail || "Couldn't update Favourite Sellers");
      }
    },
  });

  return (
    <section data-testid="ad-seller-card">
      <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400 mb-4">About the seller</h3>
      <div className="text-center">
          <div className="w-16 h-16 mx-auto rounded-full bg-orange-100 text-orange-600 grid place-items-center font-bold text-2xl overflow-hidden" data-testid="ad-seller-avatar">
            {s.photo ? (
              <img src={s.photo} alt={sellerName} className="w-full h-full object-cover" />
            ) : (
              initialOf(sellerName)
            )}
          </div>
          <div className="mt-2 font-semibold text-slate-800 inline-flex items-center gap-2 justify-center flex-wrap" data-testid="ad-seller-name">
            <span>{sellerName}</span>
            {isBusiness && (
              <span
                data-testid="ad-seller-business-chip"
                title="Verified business seller"
                className="inline-flex items-center gap-1 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest"
              >
                <Building2 className="w-3 h-3" /> Business
              </span>
            )}
          </div>
          {feedbackPct != null && (
            <div className="text-sm text-slate-600 mt-0.5" data-testid="ad-seller-feedback">
              {Math.round(feedbackPct)}% positive feedback
              {feedbackCount != null && <span className="text-slate-500"> ({feedbackCount})</span>}
            </div>
          )}

          <dl className="mt-4 border-t border-subtle divide-y divide-subtle text-left">
            {location && (
              <div className="flex justify-between py-2 text-sm">
                <dt className="text-slate-500">Location</dt>
                <dd className="font-semibold text-slate-800">{location}</dd>
              </div>
            )}
            {memberSinceLabel && (
              <div className="flex justify-between py-2 text-sm" data-testid="ad-seller-member-since">
                <dt className="text-slate-500">Member since</dt>
                <dd className="font-semibold text-slate-800">{memberSinceLabel}</dd>
              </div>
            )}
          </dl>

          {sellerId && (
            <Link
              to={`/classifieds/listings?seller_id=${sellerId}`}
              data-testid="ad-seller-other-listings"
              className="mt-3 flex items-center justify-between text-sm font-semibold text-[color:var(--brand-blue)] hover:underline py-1.5"
            >
              <span>
                View seller&apos;s other listings
                {typeof otherCount === "number" && otherCount > 0 && (
                  <span className="text-slate-500"> ({otherCount})</span>
                )}
              </span>
              <ChevronRight className="w-4 h-4" />
            </Link>
          )}

          <button
            type="button"
            onClick={() => {
              if (!user) { toast.info("Sign in to save Favourite Sellers."); return; }
              if (!sellerId) return;
              favMut.mutate();
            }}
            disabled={favMut.isPending || !sellerId}
            data-testid="ad-favourite-seller-btn"
            className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded bg-[color:var(--brand-blue)] hover:bg-[color:var(--brand-blue-hover)] text-white font-bold px-4 py-2.5 text-sm disabled:opacity-60 transition-colors"
          >
            <Heart className={`w-4 h-4 ${isFavourite ? "fill-current" : ""}`} />
            {isFavourite ? "Favourite Seller" : "Add to Favourite Sellers"}
          </button>

          <a
            href="/p/safe-buying"
            data-testid="ad-safe-buying-link"
            className="mt-3 inline-block text-sm font-semibold text-[color:var(--brand-blue)] hover:underline"
          >
            Read our safe buying advice
          </a>

          {sellerId && (
            <SellerFeedbackWidget sellerId={sellerId} listingId={listing.id} />
          )}
        </div>
    </section>
  );
}
